/**
 * Ergonomic constructors for IR nodes.
 *
 * A block compiler is a pure function that returns data; without these it
 * reads as a wall of object literals. Nothing here adds semantics — every
 * builder is a thin, typed constructor — so a consumer that would rather
 * emit plain JSON (or generate it from another language) loses nothing.
 */
export function inlines(input) {
    if (typeof input === 'string')
        return input === '' ? [] : [{ type: 'text', text: input }];
    if (Array.isArray(input))
        return input.flatMap((i) => inlines(i));
    return [input];
}
export const t = (text) => ({ type: 'text', text });
export const strong = (c) => ({ type: 'strong', content: inlines(c) });
export const em = (c) => ({ type: 'emphasis', content: inlines(c) });
export const br = () => ({ type: 'lineBreak' });
/** A run-in field label ("Avtalsnummer:") printed before its value. */
export const fieldLabel = (c) => ({ type: 'label', content: inlines(c) });
export const img = (src, opts = {}) => ({ type: 'image', src, ...opts });
export function val(text, opts = {}) {
    return { type: 'value', text: text === '' ? null : text, ...opts };
}
export const sectionRef = (target, display = 'number') => ({
    type: 'sectionRef',
    target,
    display,
});
export const attachmentRef = (target, display = 'numberAndLabel') => ({ type: 'attachmentRef', target, display });
/* ---------------------------------------------------------------- */
export function p(content, opts = {}) {
    return { type: 'paragraph', content: inlines(content), ...opts };
}
export function h(level, content, opts = {}) {
    return { type: 'heading', level, content: inlines(content), ...opts };
}
export function section(ref, title, content, opts = {}) {
    return { type: 'section', ref, title, content, ...opts };
}
export function list(items, opts = {}) {
    return {
        type: 'list',
        items: items.map((i) => ({ content: Array.isArray(i) && isBlockArray(i) ? i : [p(i)] })),
        ...opts,
    };
}
function isBlockArray(v) {
    return v.every((n) => typeof n === 'object' && n !== null && 'type' in n && isBlockType(n.type));
}
const BLOCK_TYPES = new Set([
    'section', 'heading', 'paragraph', 'list', 'optionList', 'table', 'detailList',
    'signature', 'attachmentList', 'notice', 'group', 'spacer', 'divider', 'pageBreak',
]);
const isBlockType = (v) => BLOCK_TYPES.has(v);
export function option(checked, content, opts = {}) {
    return { checked, content: inlines(content), ...opts };
}
/**
 * A tick box printed inside running text or a label/value row, bound to the
 * boolean field `sourceKey` so the tick follows the form, not the wording.
 */
export function checkbox(checked, sourceKey, mark = '☒') {
    return { type: 'value', text: checked ? mark : '☐', raw: checked, format: 'checkbox', ...(sourceKey ? { sourceKey } : {}) };
}
export function optionList(items, opts = {}) {
    return { type: 'optionList', items, ...opts };
}
export function table(columns, rows, opts = {}) {
    return {
        type: 'table',
        columns: columns.map((c, i) => (typeof c === 'string' ? { key: `c${i}`, header: inlines(c) } : c)),
        rows: rows.map((r) => Array.isArray(r)
            ? { cells: r.map((cell) => ({ content: Array.isArray(cell) && isBlockArray(cell) ? cell : [p(cell)] })) }
            : r),
        ...opts,
    };
}
export function details(items, opts = {}) {
    return {
        type: 'detailList',
        items: items.map(([term, desc]) => ({
            term: inlines(term),
            description: Array.isArray(desc) && isBlockArray(desc) ? desc : [p(desc)],
        })),
        ...opts,
    };
}
export function signature(panels, opts = {}) {
    return { type: 'signature', panels, ...opts };
}
export const attachmentList = (opts = {}) => ({ type: 'attachmentList', ...opts });
export function notice(content, opts = {}) {
    return {
        type: 'notice',
        content: Array.isArray(content) && isBlockArray(content) ? content : [p(content)],
        ...opts,
    };
}
export function group(content, opts = {}) {
    return { type: 'group', content, ...opts };
}
export const spacer = (size = 'md') => ({ type: 'spacer', size });
export const divider = () => ({ type: 'divider' });
export const pageBreak = () => ({ type: 'pageBreak' });
/** Drops `null`/`undefined`/`false` entries — the compiler's conditional idiom. */
export function nodes(...items) {
    return items.filter((n) => Boolean(n));
}
//# sourceMappingURL=builders.js.map